import React, { useMemo, useRef, useEffect } from 'react';
import { formatEventTime } from '@/lib/eventTime';
import {
  itemsForDay, packLanes, isToday, sameDay,
  DAY_START_HOUR, DAY_END_HOUR, HOUR_H, minutesFromDayStart,
} from './calendarItems';

const HOURS = Array.from({ length: DAY_END_HOUR - DAY_START_HOUR + 1 }, (_, i) => DAY_START_HOUR + i);

function hourLabel(h) {
  const am = h < 12 || h === 24;
  const base = h % 12 === 0 ? 12 : h % 12;
  return `${base} ${am ? 'AM' : 'PM'}`;
}

function TimedBlock({ ev, onSelect }) {
  const topMin = Math.max(0, minutesFromDayStart(ev.start));
  const endMin = Math.min((DAY_END_HOUR - DAY_START_HOUR) * 60, minutesFromDayStart(ev.end));
  const top = (topMin / 60) * HOUR_H;
  const height = Math.max(22, ((endMin - topMin) / 60) * HOUR_H - 2);
  const widthPct = 100 / ev._lanes;
  const leftPct = ev._lane * widthPct;
  return (
    <button
      type="button"
      onClick={() => onSelect(ev)}
      data-testid={`timegrid-item-${ev.key}`}
      className={`absolute overflow-hidden rounded-md border px-1.5 py-1 text-left text-[11px] shadow-sm transition hover:z-10 hover:shadow-md ${ev.theme.soft}`}
      style={{ top, height, left: `calc(${leftPct}% + 2px)`, width: `calc(${widthPct}% - 4px)` }}
      title={ev.title}
    >
      <div className="flex items-center gap-1">
        <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${ev.theme.dot}`} />
        <span className="truncate font-medium">{ev.title}</span>
      </div>
      {height > 34 && (
        <div className="truncate opacity-70">{formatEventTime(ev.start.toISOString(), ev.raw?.timezone)}</div>
      )}
    </button>
  );
}

export default function TimeGrid({ days, items, onSelect, testId = 'calendar-time-grid' }) {
  const scrollRef = useRef(null);
  const nowMin = (() => {
    const n = new Date();
    return (n.getHours() - DAY_START_HOUR) * 60 + n.getMinutes();
  })();

  // Scroll to 8am (or now) on mount so the grid opens on the working day.
  useEffect(() => {
    if (scrollRef.current) {
      const target = Math.max(0, ((Math.max(nowMin / 60, 2)) - 1) * HOUR_H);
      scrollRef.current.scrollTop = target;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [days?.[0]?.toISOString()]);

  const perDay = useMemo(() => days.map((d) => {
    const di = itemsForDay(items, d);
    return { day: d, allDay: di.filter((i) => i.allDay), timed: packLanes(di) };
  }), [days, items]);

  const gridHeight = (DAY_END_HOUR - DAY_START_HOUR) * HOUR_H;
  const showNowLine = days.some((d) => isToday(d)) && nowMin >= 0 && nowMin <= (DAY_END_HOUR - DAY_START_HOUR) * 60;

  return (
    <div className="overflow-hidden rounded-lg border border-slate-200 bg-white" data-testid={testId}>
      {/* Column headers */}
      <div className="flex border-b border-slate-200 bg-slate-50">
        <div className="w-14 shrink-0 border-r border-slate-200" />
        {perDay.map(({ day, allDay }) => (
          <div key={day.toISOString()} className="flex-1 border-r border-slate-100 px-2 py-2 text-center last:border-r-0">
            <div className="text-[10px] uppercase tracking-wider text-slate-500">
              {day.toLocaleDateString(undefined, { weekday: 'short' })}
            </div>
            <div className={`mx-auto mt-0.5 inline-flex h-7 min-w-7 items-center justify-center rounded-full px-1 text-sm font-semibold ${
              isToday(day) ? 'bg-slate-900 text-white' : 'text-slate-800'
            }`}>
              {day.getDate()}
            </div>
            {allDay.length > 0 && (
              <div className="mt-1 space-y-0.5">
                {allDay.map((it) => (
                  <button
                    key={it.key}
                    type="button"
                    onClick={() => onSelect(it)}
                    data-testid={`timegrid-allday-${it.key}`}
                    className={`block w-full truncate rounded border px-1 py-0.5 text-[10px] font-medium ${it.theme.soft}`}
                  >
                    {it.title}
                  </button>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Scrollable time grid */}
      <div ref={scrollRef} className="max-h-[560px] overflow-y-auto">
        <div className="flex" style={{ height: gridHeight }}>
          {/* Hour gutter */}
          <div className="w-14 shrink-0 border-r border-slate-200">
            {HOURS.slice(0, -1).map((h) => (
              <div key={h} className="relative" style={{ height: HOUR_H }}>
                <span className="absolute -top-2 right-1 text-[10px] text-slate-400">{hourLabel(h)}</span>
              </div>
            ))}
          </div>
          {/* Day columns */}
          {perDay.map(({ day, timed }) => (
            <div key={day.toISOString()} className="relative flex-1 border-r border-slate-100 last:border-r-0">
              {HOURS.slice(0, -1).map((h) => (
                <div key={h} className="border-b border-slate-100" style={{ height: HOUR_H }} />
              ))}
              {showNowLine && isToday(day) && (
                <div className="pointer-events-none absolute left-0 right-0 z-20" style={{ top: (nowMin / 60) * HOUR_H }}>
                  <div className="h-px bg-rose-500" />
                  <div className="absolute -left-1 -top-1 h-2 w-2 rounded-full bg-rose-500" />
                </div>
              )}
              {timed.map((ev) => <TimedBlock key={ev.key} ev={ev} onSelect={onSelect} />)}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
